import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { COURT, GOAL } from '../game/constants';
import { getCourtTexture, getFenceTexture, getNetTexture, getSkyTexture } from './textures';
import { createLedAdvertising } from './LedAdvertising';
import { createSeededRandom } from '../utils/random';

export interface CrowdSystem {
  mesh: THREE.InstancedMesh;
  update: (t: number, excitement?: number) => void;
}

export interface Stadium {
  group: THREE.Group;
  crowd: CrowdSystem;
  goalNetMaterials: THREE.MeshStandardMaterial[];
  updateAdvertising: (elapsed: number) => void;
}

function buildBoards(root: THREE.Group, materials: { board: THREE.Material; ad: THREE.Material }): void {
  const { halfLength, halfWidth, boardHeight, wallThickness } = COURT;
  const lenZ = halfWidth * 2;
  const lenX = halfLength * 2;

  const boardGeoV = new THREE.BoxGeometry(lenX + wallThickness, boardHeight, wallThickness);

  const make = (geo: THREE.BoxGeometry, x: number, z: number) => {
    const m = new THREE.Mesh(geo, materials.board);
    m.position.set(x, boardHeight / 2, z);
    m.castShadow = true;
    m.receiveShadow = false;
    root.add(m);
  };
  make(boardGeoV, 0, -halfWidth - wallThickness / 2);
  make(boardGeoV, 0, halfWidth + wallThickness / 2);

  const adHeight = boardHeight - 0.1;
  const adGeoLong = new THREE.PlaneGeometry(lenX + 0.4, adHeight);
  const adLong1 = new THREE.Mesh(adGeoLong, materials.ad);
  adLong1.position.set(0, boardHeight / 2, -halfWidth + 0.02);
  const adLong2 = new THREE.Mesh(adGeoLong, materials.ad);
  adLong2.position.set(0, boardHeight / 2, halfWidth - 0.02);
  adLong2.rotation.y = Math.PI;
  for (const side of [-1, 1] as const) {
    for (const seg of [-1, 1] as const) {
      const adGeoShortSeg = new THREE.PlaneGeometry(lenZ / 2 - GOAL.halfWidth - 0.2, adHeight);
      // Show one complete sign beside each goal instead of squeezing all seven into the gap.
      const index = side < 0 ? (seg < 0 ? 0 : 1) : (seg < 0 ? 5 : 6);
      const uv = adGeoShortSeg.getAttribute('uv');
      for (let i = 0; i < uv.count; i += 1) uv.setX(i, (uv.getX(i) + index) / 7);
      const ad = new THREE.Mesh(adGeoShortSeg, materials.ad);
      ad.position.set(side * (halfLength - 0.02), boardHeight / 2, seg * (GOAL.halfWidth + (lenZ / 2 - GOAL.halfWidth) / 2));
      ad.rotation.y = side < 0 ? Math.PI / 2 : -Math.PI / 2;
      root.add(ad);
    }
  }
  root.add(adLong1, adLong2);
}

function buildEndBoardsWithGoalGap(root: THREE.Group, material: THREE.Material): void {
  const { halfLength, halfWidth, boardHeight, wallThickness } = COURT;
  const segment = halfWidth - GOAL.halfWidth;
  for (const side of [-1, 1] as const) {
    for (const seg of [-1, 1] as const) {
      const geo = new THREE.BoxGeometry(wallThickness, boardHeight, segment);
      const m = new THREE.Mesh(geo, material);
      m.position.set(
        side * (halfLength + wallThickness / 2),
        boardHeight / 2,
        seg * (GOAL.halfWidth + segment / 2),
      );
      m.castShadow = true;
      m.receiveShadow = false;
      root.add(m);
    }
  }
}

function buildFence(root: THREE.Group): void {
  const { halfLength, halfWidth, boardHeight } = COURT;
  const fenceEnd = halfLength + GOAL.depth + 0.2;
  const fenceSide = halfWidth + 0.16;
  const fenceHeight = 2.8;
  const fenceTexture = getFenceTexture().clone();
  fenceTexture.needsUpdate = true;
  fenceTexture.wrapS = THREE.RepeatWrapping;
  fenceTexture.wrapT = THREE.RepeatWrapping;
  fenceTexture.repeat.set(COURT.halfLength * 1.4, fenceHeight * 0.7);
  const fenceMaterial = new THREE.MeshStandardMaterial({
    map: fenceTexture,
    transparent: true,
    alphaTest: 0.05,
    side: THREE.DoubleSide,
    color: '#cdd6dd',
    roughness: 0.4,
    metalness: 0.6,
    depthWrite: true,
  });

  const sides: Array<[number, number, number, number, number]> = [
    [fenceEnd * 2, fenceHeight, 0, -fenceSide, 0],
    [fenceEnd * 2, fenceHeight, 0, fenceSide, Math.PI],
    [fenceSide * 2, fenceHeight, -fenceEnd, 0, Math.PI / 2],
    [fenceSide * 2, fenceHeight, fenceEnd, 0, -Math.PI / 2],
  ];
  for (const [width, height, x, z, rotY] of sides) {
    const material = z > 0 ? fenceMaterial.clone() : fenceMaterial;
    if (z > 0) material.opacity = 0.18;
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(width, height), material);
    plane.name = z > 0 ? 'foreground-fence' : 'perimeter-fence';
    plane.position.set(x, boardHeight + height / 2, z);
    plane.rotation.y = rotY;
    root.add(plane);
  }

  const postGeo = new THREE.CylinderGeometry(0.045, 0.045, fenceHeight + 0.2, 6);
  const postMaterial = new THREE.MeshStandardMaterial({ color: '#9aa4ad', roughness: 0.45, metalness: 0.7 });
  const posts: Array<[number, number]> = [];
  const lengthSteps = Math.ceil(fenceEnd * 2 / 3);
  for (let i = 0; i <= lengthSteps; i += 1) {
    const x = -fenceEnd + i * fenceEnd * 2 / lengthSteps;
    posts.push([x, -fenceSide], [x, fenceSide]);
  }
  const widthSteps = Math.ceil(fenceSide * 2 / 3);
  for (let i = 1; i < widthSteps; i += 1) {
    const z = -fenceSide + i * fenceSide * 2 / widthSteps;
    posts.push([-fenceEnd, z], [fenceEnd, z]);
  }
  const postMesh = new THREE.InstancedMesh(postGeo, postMaterial, posts.length);
  const matrix = new THREE.Matrix4();
  posts.forEach(([x, z], i) => {
    const height = z === fenceSide ? 0.25 : fenceHeight + 0.2;
    matrix.makeScale(1, height / (fenceHeight + 0.2), 1);
    matrix.setPosition(x, boardHeight + height / 2, z);
    postMesh.setMatrixAt(i, matrix);
  });
  postMesh.castShadow = true;
  root.add(postMesh);

  const railGeo = new THREE.BoxGeometry(fenceEnd * 2 + 0.07, 0.07, 0.07);
  const railGeoZ = new THREE.BoxGeometry(0.07, 0.07, fenceSide * 2 + 0.07);
  const railMaterial = postMaterial;
  const rails = [
    new THREE.Mesh(railGeo, railMaterial),
    new THREE.Mesh(railGeo, railMaterial),
    new THREE.Mesh(railGeoZ, railMaterial),
    new THREE.Mesh(railGeoZ, railMaterial),
  ];
  const railY = boardHeight + fenceHeight;
  rails[0].position.set(0, railY, -fenceSide);
  rails[1].position.set(0, boardHeight + 0.25, fenceSide);
  rails[1].name = 'foreground-low-rail';
  rails[2].position.set(-fenceEnd, railY, 0);
  rails[3].position.set(fenceEnd, railY, 0);
  for (const rail of rails) root.add(rail);
}

function buildGoal(root: THREE.Group, side: -1 | 1, netMaterials: THREE.MeshStandardMaterial[]): void {
  const goal = new THREE.Group();
  const frameMaterial = new THREE.MeshStandardMaterial({ color: '#f6f8fa', roughness: 0.32, metalness: 0.35 });
  const postGeo = new THREE.CylinderGeometry(GOAL.postRadius, GOAL.postRadius, GOAL.height, 10);
  const crossGeo = new THREE.CylinderGeometry(GOAL.postRadius, GOAL.postRadius, GOAL.halfWidth * 2 + GOAL.postRadius * 2, 10);
  for (const s of [-1, 1] as const) {
    const post = new THREE.Mesh(postGeo, frameMaterial);
    post.position.set(0, GOAL.height / 2, s * GOAL.halfWidth);
    post.castShadow = true;
    goal.add(post);
  }
  const crossbar = new THREE.Mesh(crossGeo, frameMaterial);
  crossbar.rotation.x = Math.PI / 2;
  crossbar.position.set(0, GOAL.height, 0);
  crossbar.castShadow = true;
  goal.add(crossbar);

  const netMaterial = new THREE.MeshStandardMaterial({
    map: getNetTexture().clone(),
    transparent: true,
    opacity: 0.8,
    alphaTest: 0.25,
    forceSinglePass: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    color: '#e8edf2',
    roughness: 0.9,
  });
  netMaterial.map!.wrapS = THREE.RepeatWrapping;
  netMaterial.map!.wrapT = THREE.RepeatWrapping;
  netMaterial.map!.repeat.set(1, 1);
  netMaterials.push(netMaterial);
  // Keep 26 cm holes on every face instead of compressing one grid onto all panels.
  const netPlane = (width: number, height: number) => {
    const geometry = new THREE.PlaneGeometry(width, height);
    const uv = geometry.getAttribute('uv');
    for (let i = 0; i < uv.count; i += 1) uv.setXY(i, uv.getX(i) * width / (8 * 0.26), uv.getY(i) * height / (8 * 0.26));
    return geometry;
  };

  const back = new THREE.Mesh(netPlane(GOAL.halfWidth * 2, GOAL.height), netMaterial);
  back.position.set(GOAL.depth, GOAL.height / 2, 0);
  back.rotation.y = Math.PI / 2;
  goal.add(back);

  const top = new THREE.Mesh(netPlane(GOAL.depth, GOAL.halfWidth * 2), netMaterial);
  top.position.set(GOAL.depth / 2, GOAL.height, 0);
  top.rotation.x = Math.PI / 2;
  goal.add(top);

  for (const s of [-1, 1] as const) {
    const sideNet = new THREE.Mesh(netPlane(GOAL.depth, GOAL.height), netMaterial);
    sideNet.position.set(GOAL.depth / 2, GOAL.height / 2, s * GOAL.halfWidth);
    goal.add(sideNet);
  }

  goal.rotation.y = side < 0 ? Math.PI : 0;
  goal.position.set(side * COURT.halfLength, 0, 0);
  root.add(goal);
}

function buildStandsAndCrowd(root: THREE.Group): CrowdSystem {
  const { halfLength, halfWidth } = COURT;
  const concrete = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.94 });
  const trim = new THREE.MeshStandardMaterial({ color: '#efd07c', roughness: 0.65, metalness: 0.2 });
  const concreteParts: THREE.BufferGeometry[] = [];
  const trimParts: THREE.BufferGeometry[] = [];
  const paintConcrete = (geometry: THREE.BufferGeometry) => {
    // Baked tread/riser contrast preserves the stair profile under flat light.
    const normals = geometry.getAttribute('normal');
    const colors = new Float32Array(normals.count * 3);
    const tint = new THREE.Color('#526c7b');
    for (let i = 0; i < normals.count; i += 1) {
      const light = normals.getY(i) > 0.5 ? 1.25 : normals.getY(i) < -0.5 ? 0.55 : 0.88;
      colors[i * 3] = tint.r * light; colors[i * 3 + 1] = tint.g * light; colors[i * 3 + 2] = tint.b * light;
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  };
  const box = (parts: THREE.BufferGeometry[], w: number, h: number, d: number, x: number, y: number, z: number) => {
    const geometry = new THREE.BoxGeometry(w, h, d).translate(x, y, z);
    if (parts === concreteParts) paintConcrete(geometry);
    parts.push(geometry);
  };
  const nosing = (w: number, d: number, x: number, y: number, z: number) => {
    trimParts.push(new THREE.PlaneGeometry(w, d).rotateX(-Math.PI / 2).translate(x, y + 0.012, z));
  };
  const tiers = [1, 1.85, 2.7];
  const front = halfWidth + 4 - 0.825;
  const bays = [[-halfLength - 3.5, -10.6], [-9.4, -0.6], [0.6, 9.4], [10.6, halfLength + 3.5]];
  for (const side of [-1, 1]) {
    const section = (x0: number, x1: number, profile: Array<[number, number]>) => {
      const shape = new THREE.Shape(profile.map(([depth, y]) => new THREE.Vector2(-side * depth, y)));
      const geometry = new THREE.ExtrudeGeometry(shape, { depth: x1 - x0, steps: 1, bevelEnabled: false });
      geometry.rotateY(Math.PI / 2).translate(x0, 0, 0);
      const normals = geometry.getAttribute('normal');
      const indices: number[] = [];
      // Continuous profiles need no buried bottom faces or overlapping boxes.
      for (let i = 0; i < normals.count; i += 3) {
        if (normals.getY(i) > -0.99) indices.push(i, i + 1, i + 2);
      }
      geometry.setIndex(indices);
      paintConcrete(geometry);
      concreteParts.push(geometry);
    };
    const terraceProfile: Array<[number, number]> = [[front, 0]];
    tiers.forEach((height, row) => {
      const depth = front + row * 1.65;
      terraceProfile.push([depth, height - 0.08], [depth + 0.08, height], [depth + 1.65, height]);
    });
    terraceProfile.push([front + 4.95, 0]);
    for (const [x0, x1] of bays) {
      section(x0, x1, terraceProfile);
      tiers.forEach((height, row) => nosing(x1 - x0, 0.1, (x0 + x1) / 2, height, side * (front + row * 1.65 + 0.12)));
    }
    for (const aisle of [-10, 0, 10]) {
      const stairProfile: Array<[number, number]> = [[front, 0]];
      const stepHeights: number[] = [];
      for (let row = 0; row < tiers.length; row += 1) {
        const lower = row === 0 ? 0 : tiers[row - 1];
        for (let step = 0; step < 3; step += 1) {
          const depth = front + (row * 3 + step) * 0.55;
          const height = lower + (tiers[row] - lower) * (step + 1) / 3;
          stairProfile.push([depth, height], [depth + 0.55, height]);
          stepHeights.push(height);
          nosing(1.18, 0.075, aisle, height, side * (depth + 0.045));
        }
      }
      stairProfile.push([front + 4.95, 0]);
      section(aisle - 0.6, aisle + 0.6, stairProfile);
      const run = 4.4; const rise = stepHeights[8] - stepHeights[0];
      trimParts.push(new THREE.BoxGeometry(0.065, 0.065, Math.hypot(run, rise))
        .rotateX(-Math.atan2(rise, side * run))
        .translate(aisle - 0.51, (stepHeights[0] + stepHeights[8]) / 2 + 0.8, side * (front + 2.475)));
      for (const step of [0, 4, 8]) {
        const railTop = stepHeights[0] + rise * step / 8 + 0.8;
        const height = railTop - stepHeights[step];
        box(trimParts, 0.055, height, 0.055, aisle - 0.51, stepHeights[step] + height / 2, side * (front + step * 0.55 + 0.275));
      }
    }
    const backZ = side * (halfWidth + 8.4);
    box(concreteParts, halfLength * 2 + 7, 0.65, 0.22, 0, 3.02, backZ);
    box(trimParts, halfLength * 2 + 7, 0.08, 0.08, 0, 3.75, backZ);
    for (let x = -17; x <= 17; x += 2) box(trimParts, 0.06, 0.8, 0.06, x, 3.35, backZ);
    // Benches and their feet use the same static batches as the terraces.
    for (const x of side < 0 ? [-8, 8] : []) {
      box(trimParts, 3.4, 0.12, 0.5, x, 0.55, side * (halfWidth + 2.4));
      box(trimParts, 3.4, 0.4, 0.09, x, 0.92, side * (halfWidth + 2.6));
      for (const dx of [-1.3, 1.3]) box(concreteParts, 0.12, 0.5, 0.5, x + dx, 0.25, side * (halfWidth + 2.4));
    }
  }
  for (const [parts, material, name] of [[concreteParts, concrete, 'terraces-stairs-benches'], [trimParts, trim, 'terrace-edge-handrails']] as const) {
    const geometry = mergeGeometries(parts);
    if (!geometry) throw new Error('stadium kit merge failed');
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = name;
    mesh.receiveShadow = false;
    root.add(mesh);
    parts.forEach((part) => part.dispose());
  }

  const rng = createSeededRandom(97);
  const palette = ['#3569d8', '#e95842', '#ffd25d', '#69bdd2', '#c780aa', '#83ad68', '#e5e5d8'];
  const skins = ['#f0c39c', '#b97850', '#754530', '#d6a17b'];
  const placements: Array<{ x: number; y: number; z: number; phase: number; gesture: number; shirt: THREE.Color; skin: THREE.Color; hair: THREE.Color; scale: number }> = [];
  for (const side of [-1, 1]) {
    for (let row = 0; row < tiers.length; row += 1) {
      for (let x = -17; x <= 17; x += 0.8) {
        if ([-10, 0, 10].some((aisle) => Math.abs(x - aisle) < 0.65) || rng() < 0.12) continue;
        placements.push({ x: x + (rng() - 0.5) * 0.12, y: tiers[row] + 0.12,
          z: side * (halfWidth + 4 + row * 1.65), phase: rng() * Math.PI * 2, gesture: Math.floor(rng() * 4),
          shirt: new THREE.Color(palette[Math.floor(rng() * palette.length)]), skin: new THREE.Color(skins[Math.floor(rng() * skins.length)]),
          hair: new THREE.Color(rng() < 0.35 ? '#b68c45' : '#302724'), scale: 0.88 + rng() * 0.22 });
      }
    }
  }
  const cloth = new THREE.MeshStandardMaterial({ roughness: 0.95 });
  // Spend the face budget on its silhouette and expression, not hidden box faces.
  const headParts = [new THREE.SphereGeometry(0.145, 8, 5).scale(0.94, 1.08, 0.92),
    new THREE.PlaneGeometry(0.035, 0.025).translate(-0.052, 0.025, 0.127),
    new THREE.PlaneGeometry(0.035, 0.025).translate(0.052, 0.025, 0.127),
    new THREE.PlaneGeometry(0.065, 0.044).translate(0, -0.045, 0.13),
    new THREE.ConeGeometry(0.026, 0.065, 4).rotateX(Math.PI / 2).translate(0, 0, 0.145)];
  headParts.forEach((geometry, index) => {
    const colors = new Float32Array(geometry.getAttribute('position').count * 3);
    colors.fill(index > 0 && index < 4 ? 0.025 : 1);
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  });
  const headGeometry = mergeGeometries(headParts)!;
  headParts.forEach((geometry) => geometry.dispose());
  const capParts = [new THREE.SphereGeometry(0.151, 8, 3, 0, Math.PI * 2, 0, Math.PI * 0.57),
    new THREE.BoxGeometry(0.21, 0.025, 0.11).translate(0, -0.025, 0.125)];
  const capGeometry = mergeGeometries(capParts)!;
  capParts.forEach((geometry) => geometry.dispose());
  // Tailored shoulders and waist replace the barrel-shaped spectators. Closed
  // ends stay under the neck/hips; only silhouette rings need subdivision.
  const torsoGeometry = new THREE.LatheGeometry([
    new THREE.Vector2(0, -0.195), new THREE.Vector2(0.145, -0.195),
    new THREE.Vector2(0.165, -0.04), new THREE.Vector2(0.235, 0.13),
    new THREE.Vector2(0.085, 0.195), new THREE.Vector2(0, 0.195),
  ], 6).scale(1, 1, 0.62);
  // Bent elbows and fists, using open joined segments instead of capsule poles.
  const armParts = [
    new THREE.CylinderGeometry(0.065, 0.049, 0.19, 5, 1, true).translate(0, -0.09, 0),
    new THREE.CylinderGeometry(0.049, 0.04, 0.19, 5, 1, true).rotateX(-0.48).translate(0, -0.26, 0.043),
    new THREE.BoxGeometry(0.09, 0.095, 0.085).rotateX(-0.48).translate(0, -0.35, 0.09),
  ];
  const armGeometry = mergeGeometries(armParts)!;
  armParts.forEach(part => part.dispose());
  // Molded seat silhouettes reuse the box topology: broad hips, tapered back.
  const seatBack = new THREE.BoxGeometry(0.46, 0.4, 0.07);
  const seatPositions = seatBack.getAttribute('position');
  for (let i = 0; i < seatPositions.count; i += 1) {
    const top = seatPositions.getY(i) > 0;
    seatPositions.setX(i, seatPositions.getX(i) * (top ? 0.82 : 1));
    seatPositions.setZ(i, seatPositions.getZ(i) + (top ? -0.045 : 0));
  }
  seatBack.computeVertexNormals();
  seatBack.translate(0, 0.2, -0.22);
  const seatParts = [new THREE.BoxGeometry(0.49, 0.09, 0.46), seatBack];
  const seatGeometry = mergeGeometries(seatParts)!;
  seatParts.forEach(part => part.dispose());
  const parts = {
    torso: new THREE.InstancedMesh(torsoGeometry, cloth, placements.length),
    head: new THREE.InstancedMesh(headGeometry, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95 }), placements.length),
    hair: new THREE.InstancedMesh(capGeometry, cloth, placements.length),
    legs: new THREE.InstancedMesh(mergeGeometries([
      new THREE.CylinderGeometry(0.079, 0.051, 0.35, 5, 1, true).rotateX(-0.17).translate(-0.095, 0, 0),
      new THREE.CylinderGeometry(0.079, 0.051, 0.35, 5, 1, true).rotateX(-0.17).translate(0.095, 0, 0),
      new THREE.BoxGeometry(0.15, 0.09, 0.24).translate(-0.095, -0.16, 0.075),
      new THREE.BoxGeometry(0.15, 0.09, 0.24).translate(0.095, -0.16, 0.075),
    ])!, cloth, placements.length),
    armL: new THREE.InstancedMesh(armGeometry, cloth, placements.length),
    armR: new THREE.InstancedMesh(armGeometry, cloth, placements.length),
    seats: new THREE.InstancedMesh(seatGeometry, cloth, placements.length),
  };
  for (const [name, mesh] of Object.entries(parts)) {
    mesh.name = `crowd-${name}`;
    mesh.castShadow = false;
    if (name !== 'seats') mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    root.add(mesh);
  }
  const dummy = new THREE.Object3D();
  const bodyRotation = new THREE.Quaternion();
  const axisY = new THREE.Vector3(0, 1, 0);
  const local = new THREE.Object3D();
  const base = new THREE.Matrix4();
  const matrix = new THREE.Matrix4();
  const batches = Object.values(parts);
  const armAngles = new Float32Array(placements.length * 3);
  const jumpAmounts = new Float32Array(placements.length);
  const leanAngles = new Float32Array(placements.length);
  const turn = Math.PI * 2;
  const angleDelta = (from: number, to: number) => ((to - from + Math.PI) % turn + turn) % turn - Math.PI;
  let crowdFrame = -1;
  let crowdEnergy = 0;
  const set = (mesh: THREE.InstancedMesh, i: number, x: number, y: number, z: number, rz = 0, rx = 0, ry = 0) => {
    local.position.set(x, y, z); local.rotation.set(rx, ry, rz); local.scale.set(1, 1, 1);
    if (mesh === parts.hair) local.scale.set(placements[i].gesture === 2 ? 1.2 : 1, placements[i].gesture === 0 ? 0.6 : 1.1, placements[i].gesture === 3 ? 0.75 : 1);
    if (mesh === parts.torso) local.scale.x = placements[i].gesture === 2 ? 1.2 : 1;
    local.updateMatrix();
    matrix.multiplyMatrices(base, local.matrix); mesh.setMatrixAt(i, matrix);
  };
  const update = (t: number, excitement = 0) => {
    const frame = Math.floor(t * 24);
    if (frame === crowdFrame) return;
    const first = crowdFrame < 0;
    const dt = first ? 1 / 24 : Math.max(0, Math.min(0.1, (frame - crowdFrame) / 24));
    crowdFrame = frame;
    // Excitement changes amplitude, never the clock: late-match cheers cannot rewind a pose.
    crowdEnergy += (THREE.MathUtils.clamp(excitement, 0, 1) - crowdEnergy) * (1 - Math.exp(-12 * dt));
    const poseBlend = first ? 1 : 1 - Math.exp(-14 * dt);
    const maxArmStep = 8 * dt;
    const sampleTime = frame / 24;
    for (let i = 0; i < placements.length; i++) {
      const p = placements[i];
      const beat = sampleTime * (2.45 + p.phase * 0.055) + p.phase;
      const bounce = Math.sin(beat);
      const hop = Math.max(0, bounce);
      const crouch = Math.max(0, -bounce);
      const gesture = (p.gesture + Math.floor((sampleTime + p.phase) / 6.5)) % 6;
      const travel = Math.max(0, Math.cos(sampleTime * 0.8 - p.x * 0.14 + (p.z > 0 ? 1.3 : 0)));
      const crest = travel * travel * travel * travel;
      const waveLift = crest * crest * crest;
      const enthusiasm = crowdEnergy * (0.6 + p.phase * 0.06);
      const jumpAmount = gesture === 2 ? 0.16 + enthusiasm * 0.2 : enthusiasm * 0.22;
      jumpAmounts[i] += (jumpAmount - jumpAmounts[i]) * poseBlend;
      const jumpHeight = hop * hop * jumpAmounts[i] + waveLift * 0.06;
      const compress = -crouch * (gesture === 2 ? 0.035 : 0.015) * (0.4 + crowdEnergy);
      const sway = Math.sin(beat * 0.55) * (0.035 + crowdEnergy * 0.025);
      const twist = gesture === 4 ? bounce * 0.1 : Math.sin(beat * 0.35) * 0.035;
      bodyRotation.setFromAxisAngle(axisY, (p.z > 0 ? Math.PI : 0) + twist * 0.35);
      dummy.position.set(p.x, p.y + jumpHeight, p.z);
      dummy.quaternion.copy(bodyRotation);
      dummy.scale.setScalar(p.scale);
      dummy.updateMatrix(); base.copy(dummy.matrix);
      const clap = 0.5 + Math.sin(sampleTime * 8.5 + p.phase) * 0.5;
      const nod = Math.sin(beat * 1.6) * (gesture === 1 ? 0.055 : 0.02);
      const targetLean = gesture === 5 ? -0.12 : gesture === 1 ? 0.08 + clap * 0.04 : -crowdEnergy * 0.06;
      leanAngles[i] += (targetLean - leanAngles[i]) * poseBlend;
      const lean = leanAngles[i];
      const headTurn = Math.sin(beat * 0.26) * 0.16;
      set(parts.torso, i, 0, 0.62 + compress, 0, sway, lean, twist);
      set(parts.head, i, -sway * 0.2, 0.97 + compress, 0.025, sway * 0.5, nod - lean * 0.4, headTurn);
      set(parts.hair, i, -sway * 0.2, 1 + compress, 0.025, sway * 0.4, nod * 0.7 - lean * 0.4, headTurn);
      set(parts.legs, i, 0, 0.24 + compress * 0.4, 0.015, -sway * 0.3, jumpHeight * 0.18);
      let left = -0.15 + sway, right = 0.15 + sway, armPitch = -0.08;
      if (gesture === 1) {
        // Positive left / negative right brings the fists together in front of the chest.
        left = 0.24 + clap * 0.34; right = -left; armPitch = -1.14;
      } else if (gesture === 2) {
        left = -2.55 + bounce * 0.14; right = 2.55 - bounce * 0.14;
      } else if (gesture === 3) {
        if (p.phase < Math.PI) left = -2.5 + Math.sin(beat * 2.1) * 0.28;
        else right = 2.5 + Math.sin(beat * 2.1) * 0.28;
      } else if (gesture === 4) {
        left = -1.6 - hop * 0.65; right = 1.6 + crouch * 0.65; armPitch = -0.3;
      } else if (gesture === 5) {
        left = 2.45; right = -2.45; armPitch = -0.1;
      }
      const lift = Math.max(waveLift * 0.92, enthusiasm * 0.65);
      // Raised/shocked poses straddle +/- PI: both blends must follow the short arc.
      left += angleDelta(left, -2.7) * lift;
      right += angleDelta(right, 2.7) * lift;
      const armIndex = i * 3;
      for (let axis = 0; axis < 3; axis++) {
        const target = axis === 0 ? left : axis === 1 ? right : armPitch;
        const current = armAngles[armIndex + axis];
        const step = angleDelta(current, target) * poseBlend;
        armAngles[armIndex + axis] = first ? target : current + THREE.MathUtils.clamp(step, -maxArmStep, maxArmStep);
      }
      set(parts.armL, i, -0.19, 0.77 + compress, 0, armAngles[armIndex], armAngles[armIndex + 2]);
      set(parts.armR, i, 0.19, 0.77 + compress, 0, armAngles[armIndex + 1], armAngles[armIndex + 2]);
      // Seats stay fixed while fans bounce and wave independently.
      if (first) {
        dummy.position.y = p.y; dummy.updateMatrix(); base.copy(dummy.matrix);
        set(parts.seats, i, 0, 0.15, -0.09);
      }
    }
    batches.forEach((mesh) => { if (first || mesh !== parts.seats) mesh.instanceMatrix.needsUpdate = true; });
  };
  placements.forEach((p, i) => {
    parts.torso.setColorAt(i, p.shirt); parts.head.setColorAt(i, p.skin); parts.hair.setColorAt(i, p.gesture === 0 ? p.shirt : p.hair);
    parts.armL.setColorAt(i, p.skin); parts.armR.setColorAt(i, p.skin);
    parts.legs.setColorAt(i, new THREE.Color('#252e40')); parts.seats.setColorAt(i, new THREE.Color(Math.floor((p.x + 18) / 4) % 2 ? '#2584ba' : '#df6544'));
  });
  update(0);
  Object.values(parts).forEach((mesh) => { if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true; });
  return { mesh: parts.torso, update };
}

function buildCarnivalKit(root: THREE.Group): void {
  const pieces: THREE.BufferGeometry[] = [];
  const color = new THREE.Color();
  const add = (geometry: THREE.BufferGeometry, tint: string, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0) => {
    geometry.rotateX(rx).rotateY(ry).rotateZ(rz).translate(x, y, z);
    color.set(tint);
    const colors = new Float32Array(geometry.getAttribute('position').count * 3);
    for (let i = 0; i < colors.length; i += 3) { colors[i] = color.r; colors[i + 1] = color.g; colors[i + 2] = color.b; }
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    pieces.push(geometry);
  };
  const box = (w: number, h: number, d: number, tint: string, x: number, y: number, z: number, rz = 0) => add(new THREE.BoxGeometry(w, h, d), tint, x, y, z, 0, 0, rz);
  const blue = '#2584ba'; const red = '#df6544'; const gold = '#ffcc57'; const cream = '#f1dfb1';
  // All decoration stays behind the play area or beyond the short ends.
  for (const side of [-1, 1]) {
    const x = side * 17.8; const tint = side < 0 ? blue : red;
    // A chunky entrance gate doubles as the team landmark.
    for (const dx of [-0.9, 0.9]) { box(0.32, 2.6, 0.4, tint, x + dx, 1.3, -10.2); box(0.5, 0.22, 0.6, gold, x + dx, 0.15, -10.2); }
    box(2.35, 0.5, 0.48, tint, x, 2.7, -10.2);
    box(2, 0.1, 0.52, cream, x, 2.98, -10.2);
    add(new THREE.IcosahedronGeometry(0.63, 1), cream, x, 3.55, -10.2);
    add(new THREE.IcosahedronGeometry(0.24, 0), '#30384a', x, 3.58, -9.68);
    // Corner bumpers and striped traffic cones give the arena a street setting.
    for (const z of [-8.8, 8.8]) {
      box(0.5, 0.9, 0.5, tint, side * 15.45, 0.45, z);
      box(0.54, 0.1, 0.54, gold, side * 15.45, 0.86, z);
      add(new THREE.ConeGeometry(0.19, 0.58, 7), '#ee8145', x, 0.31, z);
      add(new THREE.CylinderGeometry(0.095, 0.125, 0.1, 7), cream, x, 0.35, z);
      box(0.43, 0.055, 0.43, '#34404a', x, 0.03, z);
    }
    // Dugout canopy: ribs, tilted roof strips, a back panel and seats.
    const benchX = side * 8;
    for (const dx of [-1.65, 1.65]) box(0.09, 1.65, 0.09, cream, benchX + dx, 0.83, -11.3);
    for (let strip = 0; strip < 7; strip += 1) {
      const roof = new THREE.BoxGeometry(0.5, 0.13, 1.45);
      const roofPositions = roof.getAttribute('position');
      for (let vertex = 0; vertex < roofPositions.count; vertex += 1) {
        const front = roofPositions.getZ(vertex) > 0;
        roofPositions.setY(vertex, roofPositions.getY(vertex) + (front ? 0.13 : -0.08));
        if (front) roofPositions.setX(vertex, roofPositions.getX(vertex) * 1.06);
      }
      roof.computeVertexNormals();
      add(roof, strip % 2 ? cream : tint, benchX - 1.5 + strip * 0.5, 1.73, -10.95);
    }
    box(3.5, 0.55, 0.08, tint, benchX, 0.85, -11.32);
    // Grated drains are baked into the same geometry batch.
    for (const z of [-4, 0, 4]) {
      box(0.44, 0.03, 0.7, '#202c35', side * 15.65, 0.02, z);
      for (let bar = 0; bar < 5; bar += 1) box(0.4, 0.02, 0.045, '#70858d', side * 15.65, 0.045, z - 0.25 + bar * 0.12);
    }
  }
  // Alternating pennants and massive team crests make a carnival silhouette.
  for (let i = 0; i < 35; i += 1) {
    const x = -17 + i;
    add(new THREE.ConeGeometry(0.22, 0.52, 3), i % 2 ? red : blue, x, 4.05 - Math.sin(i / 34 * Math.PI) * 0.3, -17.1, 0, 0, Math.PI);
  }
  box(35, 0.045, 0.045, cream, 0, 4.35, -17.1);
  // Sculpted rooftop silhouettes replace a flat horizon without extra lights.
  for (const side of [-1, 1]) {
    for (let i = 0; i < 4; i += 1) {
      const x = side * (24 + i * 4); const z = -24 - i * 2; const h = 7 + i * 1.8;
      box(4, h, 3.5, i % 2 ? '#536b80' : '#83635d', x, h / 2, z);
      box(4.3, 0.3, 3.8, cream, x, h, z);
      add(new THREE.CylinderGeometry(0.55, 0.72, 1.4, 8), '#627e88', x, h + 0.85, z);
      add(new THREE.ConeGeometry(0.8, 0.4, 8), '#364b5d', x, h + 1.7, z);
      box(0.055, 1.4, 0.055, gold, x + 1.3, h + 0.8, z);
    }
  }
  // Indexed/non-indexed primitives share the same attributes before merging.
  const normalized = pieces.map((part) => part.index ? part.toNonIndexed() : part.clone());
  const geometry = mergeGeometries(normalized);
  if (!geometry) throw new Error('carnival stadium kit merge failed');
  const mesh = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.83 }));
  mesh.name = 'carnival-street-kit';
  mesh.userData.propFamilies = ['entrance-gates', 'football-crests', 'corner-bumpers', 'traffic-cones', 'dugout-canopies', 'drain-grates', 'pennants', 'rooftop-water-towers'];
  mesh.receiveShadow = false;
  root.add(mesh);
  pieces.forEach((part) => part.dispose()); normalized.forEach((part) => part.dispose());
}

function buildSkyline(root: THREE.Group): void {
  const rng = createSeededRandom(55);
  const windowCanvas = document.createElement('canvas');
  windowCanvas.width = 256;
  windowCanvas.height = 256;
  const context = windowCanvas.getContext('2d');
  if (!context) return;
  // Four facade strips share one texture; frames and recesses cost no geometry.
  for (let style = 0; style < 4; style += 1) {
    const origin = style * 64;
    context.fillStyle = '#bcc6cd'; context.fillRect(origin, 0, 64, 256);
    for (let y = 0; y < 256; y += 16) {
      context.fillStyle = '#8999a5'; context.fillRect(origin, y, 64, 2);
      context.fillStyle = '#e0e3dc'; context.fillRect(origin, y + 2, 64, 1);
      for (let column = 0; column < 4; column += 1) {
        const x = origin + 6 + column * 14;
        const width = style === 2 ? 10 : 8;
        context.fillStyle = '#6e808e'; context.fillRect(x - 1, y + 4, width + 2, 9);
        context.fillStyle = rng() < 0.28 ? '#ffce8a' : '#263f53';
        context.fillRect(x, y + 5, width, 6);
        context.fillStyle = '#152d40'; context.fillRect(x, y + 5, width, 1);
        context.fillStyle = '#dce2dd'; context.fillRect(x - 2, y + 12, width + 4, 1);
        if (style === 1 || style === 3) {
          context.fillStyle = '#738793'; context.fillRect(x + width / 2, y + 5, 1, 6);
        }
        if (style === 3) {
          context.fillStyle = '#4e6677'; context.fillRect(x - 2, y + 13, width + 4, 2);
          context.fillStyle = '#cfdbd7'; context.fillRect(x - 2, y + 13, width + 4, 1);
        }
      }
    }
    // Unpainted gutter supplies solid cornices and roofs in this same atlas.
    context.fillStyle = '#ffffff'; context.fillRect(origin, 0, 3, 256);
  }
  const texture = new THREE.CanvasTexture(windowCanvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.MeshStandardMaterial({ map: texture, vertexColors: true, roughness: 0.92 });
  const positions: number[] = []; const uvs: number[] = []; const colors: number[] = [];
  const tint = new THREE.Color(); const point = new THREE.Vector3();
  const palette = ['#a2b7c5', '#ccad94', '#a3b7a6', '#bfa2a4'];
  const footprint = [[-0.4, -0.5], [0.4, -0.5], [0.5, -0.4], [0.5, 0.4],
    [0.4, 0.5], [-0.4, 0.5], [-0.5, 0.4], [-0.5, -0.4]];
  const transform = new THREE.Matrix4();
  const vertex = (x: number, y: number, z: number, u: number, v: number) => {
    point.set(x, y, z).applyMatrix4(transform);
    positions.push(point.x, point.y, point.z); uvs.push(u, v); colors.push(tint.r, tint.g, tint.b);
  };
  // Chamfered footprints, roof setbacks and hipped crowns replace box skylines.
  // All twelve buildings are static and merged into the original single draw.
  const dummy = new THREE.Object3D();
  for (let i = 0; i < 12; i += 1) {
    const w = 5 + rng() * 7;
    const h = 6 + rng() * 12;
    const depth = w * (0.65 + rng() * 0.25);
    const angle = (i / 12) * Math.PI * 2 + rng() * 0.3;
    const dist = 34 + rng() * 16;
    dummy.position.set(Math.cos(angle) * dist, -0.5, Math.sin(angle) * dist);
    dummy.rotation.y = rng() * Math.PI;
    dummy.updateMatrix(); transform.copy(dummy.matrix);
    const style = i % 4;
    const facadeTop = h * (style === 1 ? 0.83 : 0.86);
    const rings = style === 1
      ? [[0, w, depth], [facadeTop, w, depth], [facadeTop + 0.16, w * 1.05, depth * 1.05], [h, w * 0.86, depth * 0.12]]
      : [[0, w, depth], [facadeTop, w, depth], [facadeTop + 0.16, w * 1.05, depth * 1.05],
        [facadeTop + 0.2, w * (style === 2 ? 0.82 : 0.67), depth * (style === 2 ? 0.82 : 0.7)],
        [h, w * (style === 2 ? 0.82 : 0.67), depth * (style === 2 ? 0.82 : 0.7)]];
    for (let layer = 0; layer < rings.length - 1; layer += 1) {
      const [lowerY, lowerW, lowerD] = rings[layer];
      const [upperY, upperW, upperD] = rings[layer + 1];
      for (let face = 0; face < footprint.length; face += 1) {
        const [ax, az] = footprint[face]; const [bx, bz] = footprint[(face + 1) % footprint.length];
        const facade = layer === 0 && face % 2 === 0;
        tint.set(layer === 0 ? palette[style] : layer === 1 ? '#e4d7b9' : '#61798a');
        tint.multiplyScalar(face === 0 || face === 6 ? 0.83 : 1);
        const u0 = (style * 64 + (facade ? 4 : 1)) / 256;
        const u1 = (style * 64 + (facade ? 62 : 1)) / 256;
        const v0 = facade ? 0 : 0.5; const v1 = facade ? Math.min(1, h / 22) : 0.5;
        vertex(ax * lowerW, lowerY, az * lowerD, u0, v0);
        vertex(ax * upperW, upperY, az * upperD, u0, v1);
        vertex(bx * upperW, upperY, bz * upperD, u1, v1);
        vertex(ax * lowerW, lowerY, az * lowerD, u0, v0);
        vertex(bx * upperW, upperY, bz * upperD, u1, v1);
        vertex(bx * lowerW, lowerY, bz * lowerD, u1, v0);
      }
    }
    const [roofY, roofW, roofD] = rings[rings.length - 1];
    tint.set('#7891a0');
    for (let face = 0; face < footprint.length; face += 1) {
      const [ax, az] = footprint[face]; const [bx, bz] = footprint[(face + 1) % footprint.length];
      const u = (style * 64 + 1) / 256;
      vertex(0, roofY, 0, u, 0.5);
      vertex(bx * roofW, roofY, bz * roofD, u, 0.5);
      vertex(ax * roofW, roofY, az * roofD, u, 0.5);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.computeVertexNormals(); geometry.computeBoundingSphere();
  const buildings = new THREE.Mesh(geometry, material);
  buildings.name = 'skyline-blocks';
  buildings.userData.architecture = ['chamfered-corners', 'setback-crowns', 'hipped-roofs', 'facade-sills'];
  root.add(buildings);
}

function buildFloodlights(root: THREE.Group): void {
  const poleMaterial = new THREE.MeshStandardMaterial({ color: '#3a4149', roughness: 0.55, metalness: 0.5 });
  const lampMaterial = new THREE.MeshStandardMaterial({
    color: '#fff7d6',
    emissive: '#fff2c0',
    emissiveIntensity: 2.4,
    roughness: 0.4,
  });
  const positions: Array<[number, number]> = [
    [-(COURT.halfLength + 3.4), -(COURT.halfWidth + 4.5)],
    [COURT.halfLength + 3.4, -(COURT.halfWidth + 4.5)],
    [-(COURT.halfLength + 3.4), COURT.halfWidth + 4.5],
    [COURT.halfLength + 3.4, COURT.halfWidth + 4.5],
  ];
  const poles = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.16, 0.22, 9.5, 8), poleMaterial, positions.length);
  const panels = new THREE.InstancedMesh(new THREE.BoxGeometry(2.4, 1.1, 0.3), poleMaterial, positions.length);
  const lamps = new THREE.InstancedMesh(new THREE.BoxGeometry(0.44, 0.44, 0.14), lampMaterial, positions.length * 4);
  const rig = new THREE.Object3D();
  const localLamp = new THREE.Matrix4();
  const lampMatrix = new THREE.Matrix4();
  poles.castShadow = true;
  let lampIndex = 0;
  for (let index = 0; index < positions.length; index += 1) {
    const [x, z] = positions[index];
    rig.position.set(x, 4.75, z);
    rig.rotation.set(0, 0, 0);
    rig.updateMatrix();
    poles.setMatrixAt(index, rig.matrix);
    rig.position.set(x, 9.4, z);
    rig.lookAt(x * 0.25, 0, z * 0.25);
    rig.updateMatrix();
    panels.setMatrixAt(index, rig.matrix);
    for (let i = 0; i < 4; i += 1) {
      localLamp.makeTranslation(-0.81 + i * 0.54, 0, 0.2);
      lampMatrix.multiplyMatrices(rig.matrix, localLamp);
      lamps.setMatrixAt(lampIndex++, lampMatrix);
    }

    const spot = new THREE.SpotLight('#fff3cf', 55, 60, 0.62, 0.5, 1.6);
    spot.position.set(x, 9.4, z);
    spot.target.position.set(x * 0.2, 0, z * 0.2);
    root.add(spot, spot.target);
  }
  poles.instanceMatrix.needsUpdate = true;
  panels.instanceMatrix.needsUpdate = true;
  lamps.instanceMatrix.needsUpdate = true;
  root.add(poles, panels, lamps);
}

export function buildStadium(): Stadium {
  const group = new THREE.Group();
  const netMaterials: THREE.MeshStandardMaterial[] = [];

  const floorTexture = getCourtTexture();
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(COURT.halfLength * 2, COURT.halfWidth * 2),
    new THREE.MeshStandardMaterial({ map: floorTexture, roughness: 0.94, metalness: 0 }),
  );
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  // Opaque players write depth first, so hidden grass pixels skip lighting and shadow sampling.
  floor.renderOrder = 1;
  group.add(floor);

  const apron = new THREE.Mesh(
    new THREE.PlaneGeometry(120, 120),
    new THREE.MeshStandardMaterial({ color: '#20242b', roughness: 0.95 }),
  );
  apron.rotation.x = -Math.PI / 2;
  apron.position.y = -0.03;
  apron.receiveShadow = false;
  apron.renderOrder = 2;
  group.add(apron);

  const boardMaterial = new THREE.MeshStandardMaterial({ color: '#2c3340', roughness: 0.6 });
  const advertising = createLedAdvertising();
  buildBoards(group, { board: boardMaterial, ad: advertising.material });
  buildEndBoardsWithGoalGap(group, boardMaterial);
  buildFence(group);
  buildGoal(group, -1, netMaterials);
  buildGoal(group, 1, netMaterials);
  const crowd = buildStandsAndCrowd(group);
  buildFloodlights(group);
  buildSkyline(group);
  buildCarnivalKit(group);

  const sky = new THREE.Mesh(
    new THREE.SphereGeometry(88, 32, 20),
    new THREE.MeshBasicMaterial({ map: getSkyTexture(), side: THREE.BackSide, fog: false }),
  );
  group.add(sky);

  return { group, crowd, goalNetMaterials: netMaterials, updateAdvertising: advertising.update };
}

export function buildTrophy(): THREE.Group {
  const trophy = new THREE.Group();
  const goldParts: THREE.BufferGeometry[] = [];
  const baseParts: THREE.BufferGeometry[] = [];
  const whiteParts: THREE.BufferGeometry[] = [];
  const blackParts: THREE.BufferGeometry[] = [];
  baseParts.push(new THREE.CylinderGeometry(0.43, 0.5, 0.18, 12).translate(0, 0.09, 0));
  goldParts.push(new THREE.CylinderGeometry(0.38, 0.43, 0.1, 12).translate(0, 0.23, 0));
  baseParts.push(new THREE.BoxGeometry(0.5, 0.4, 0.45).translate(0, 0.46, 0));
  goldParts.push(new THREE.BoxGeometry(0.34, 0.19, 0.025).translate(0, 0.47, 0.24));
  const boot = new THREE.Shape();
  boot.moveTo(-0.45, 0); boot.lineTo(0.5, 0); boot.quadraticCurveTo(0.73, 0.1, 0.5, 0.23);
  boot.lineTo(0.01, 0.32); boot.lineTo(-0.08, 0.65); boot.lineTo(-0.41, 0.65);
  boot.lineTo(-0.45, 0);
  const bootGeometry = new THREE.ExtrudeGeometry(boot, { depth: 0.35, steps: 1, bevelEnabled: true, bevelSegments: 2, bevelSize: 0.055, bevelThickness: 0.055, curveSegments: 5 });
  bootGeometry.translate(0, 0.78, -0.175); goldParts.push(bootGeometry);
  baseParts.push(new THREE.BoxGeometry(1.12, 0.09, 0.48).translate(0.05, 0.77, 0));
  for (const x of [-0.3, 0, 0.35]) for (const z of [-0.13, 0.13]) goldParts.push(new THREE.CylinderGeometry(0.055, 0.04, 0.1, 5).translate(x, 0.68, z));
  for (let i = 0; i < 4; i += 1) whiteParts.push(new THREE.BoxGeometry(0.055, 0.025, 0.32).rotateZ(-0.2).translate(-0.05 + i * 0.105, 1.11 - i * 0.021, 0));
  const ballCenter = new THREE.Vector3(0.38, 1.53, 0);
  whiteParts.push(new THREE.SphereGeometry(0.27, 16, 10).translate(...ballCenter.toArray()));
  const ico = new THREE.IcosahedronGeometry(1, 0);
  const points = ico.getAttribute('position'); const used = new Set<string>();
  const normal = new THREE.Vector3(); const target = new THREE.Vector3(); const orientation = new THREE.Quaternion();
  for (let i = 0; i < points.count; i += 1) {
    normal.fromBufferAttribute(points, i).normalize();
    const key = normal.toArray().map((value) => value.toFixed(3)).join(',');
    if (used.has(key)) continue;
    used.add(key); orientation.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
    target.copy(normal).multiplyScalar(0.267).add(ballCenter);
    blackParts.push(new THREE.CircleGeometry(0.082, 5).applyQuaternion(orientation).translate(...target.toArray()));
  }
  ico.dispose();
  const kits = [
    [goldParts, '#ffcf4d', 0.78, 'golden-comedy-boot'],
    [baseParts, '#42364b', 0.35, 'trophy-plinth-sole'],
    [whiteParts, '#fff1c6', 0.1, 'football-laces'],
    [blackParts, '#292c3a', 0, 'football-pentagons'],
  ] as const;
  for (const [parts, color, metalness, name] of kits) {
    const normalized = parts.map((part) => part.index ? part.toNonIndexed() : part.clone());
    const geometry = mergeGeometries(normalized);
    if (!geometry) throw new Error('trophy geometry merge failed');
    const mesh = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color, metalness, roughness: 0.3 }));
    mesh.name = name; mesh.castShadow = true; trophy.add(mesh);
    parts.forEach((part) => part.dispose()); normalized.forEach((part) => part.dispose());
  }
  return trophy;
}
